import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Alert from '../components/Alert';
import Spinner from '../components/Spinner';
import AIAssistant from '../components/AIAssistant';
import BCPPageTitle from '../components/BCPPageTitle';
import GuideTopAI from '../components/GuideTopAI';
import RedButton from '../components/RedButton';
import Button from '../parts/button';
import { navigationService } from '../utils/navigation';
import { exportBCP } from '../services/api';
import { useLoadingState } from '../hooks/useLoadingState';
import { useRegulationGenerationAsync } from '../hooks/useRegulationGenerationAsync';
import { useAutoDismissMessage } from '../hooks/useAutoDismissMessage';
import { useBCPLoader } from '../hooks/useBCPLoader';
import { useBeforeUnload } from '../hooks/useBeforeUnload';
import { bcpService } from '../services/bcpService';
import type { BCPSaveRequest, BCPUpdateRequest } from '../types/bcp';
import {
  BCPFormContainer,
  BCPFormLayout,
  BCPSectionCard,
  ReferenceMaterialsPanel,
} from '../components/BCP';
import { REGULATION_SECTIONS } from '../config/regulationSections';
import { useBCPDynamicForm } from '../hooks/useBCPDynamicForm';
import { useAIConsultContext } from '../hooks/useAIConsultContext';
import BasicBCPStepFlow from '../components/BasicBCPStepFlow';
import BCPDownloadModal from '../components/BCPDownloadModal';

interface NavigationState {
  uploadedFiles: any[];
  apiResponse?: any;
  restorePolicyData?: Record<string, string>;
  regulationFormData?: Record<string, string>;
  restorePolicyFormData?: Record<string, string>;
}

const REGULATION_FIELD_IDS = REGULATION_SECTIONS.flatMap((section) =>
  section.fields
    .filter((field) => field.type !== 'static')
    .map((field) => field.id)
);

const extractRegulationFormValues = (
  source?: Record<string, any>
): Record<string, string> => {
  if (!source) {
    return {};
  }

  const result: Record<string, string> = {};

  REGULATION_FIELD_IDS.forEach((fieldId) => {
    const value = source[fieldId];
    if (typeof value === 'string' && value.trim().length > 0) {
      result[fieldId] = value;
    }
  });

  return result;
};

const BCPRegulationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ bcpId?: string }>();
  const [searchParams] = useSearchParams();

  const navigationState = navigationService.extractLocationState<NavigationState>(
    location,
    {
      uploadedFiles: [],
      apiResponse: undefined,
      restorePolicyData: undefined,
      regulationFormData: undefined,
      restorePolicyFormData: undefined,
    }
  );

  const { uploadedFiles, apiResponse, restorePolicyData } = navigationState;

  const exportState = useLoadingState<Blob>();
  const [saveLoading, setSaveLoading] = useState(false);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);
  const { message: successMessage, showMessage: setSuccessMessage } =
    useAutoDismissMessage();
  const hasLoadedInitialData = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const initialValuesRef = useRef<Record<string, string>>(
    navigationState.regulationFormData ?? {}
  );
  const [initialValuesVersion, setInitialValuesVersion] = useState(0);

  const updateInitialValues = useCallback(
    (nextValues?: Record<string, string | undefined>) => {
      if (nextValues) {
        const cloned: Record<string, string> = {};
        Object.keys(nextValues).forEach((key) => {
          cloned[key] = nextValues[key] ?? '';
        });
        initialValuesRef.current = cloned;
      } else {
        initialValuesRef.current = {};
      }
      setInitialValuesVersion((prev) => {
        const newVersion = prev + 1;
        console.log('[BCPRegulationPage] updateInitialValues called:', {
          keysCount: Object.keys(initialValuesRef.current).length,
          newVersion,
        });
        return newVersion;
      });
    },
    []
  );

  const bcpIdFromPath = params.bcpId ?? 'new';
  const tenantIdFromQuery = searchParams.get('tenantId');
  const resolvedBcpId = bcpIdFromPath;
  const resolvedTenantId = tenantIdFromQuery ?? apiResponse?.tenant_id ?? null;

  // Load BCP document first
  const {
    loading: bcpLoading,
    isEditMode,
    bcpDocument,
    getInitialFormData,
  } = useBCPLoader({
    bcpId: resolvedBcpId === 'new' ? null : resolvedBcpId,
    tenantId: resolvedTenantId,
    onError: (error) => {
      setGeneralErrors([error]);
    },
  });

  // ページロード時に上部にスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
  const isNavigatingRef = useRef(false);
  const beginInternalNavigation = useCallback(() => {
    isNavigatingRef.current = true;
  }, []);

  // 離脱確認ダイアログ(変更なし/保存中/内部ナビゲーション中はスキップ)
  useBeforeUnload({
    enabled: true,
    shouldBypass: () => {
      // 「次へ」「戻る」「サブヘッダー」などアプリ内ナビゲーション中はブロックしない
      if (isNavigatingRef.current) return true;
      return !hasUnsavedChanges || isSaving || exportState.isLoading;
    },
  });

  // AI生成完了後に自動保存されたBCPを保持
  const AUTO_SAVED_BCP_KEY = `autoSavedBcp_regulation_${resolvedTenantId || 'default'}`;

  const [autoSavedBcp, setAutoSavedBcp] = useState<any>(() => {
    // ローカルストレージから自動保存されたBCPを復元
    if (resolvedBcpId === 'new') {
      const saved = localStorage.getItem(AUTO_SAVED_BCP_KEY);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error('[BCPRegulationPage] Failed to parse auto-saved BCP:', e);
        }
      }
    }
    return null;
  });

  const hasAutoSaved = useRef(false);

  // autoSavedBcpが更新されたらローカルストレージに保存
  useEffect(() => {
    if (autoSavedBcp && resolvedBcpId === 'new') {
      localStorage.setItem(AUTO_SAVED_BCP_KEY, JSON.stringify(autoSavedBcp));
    }
  }, [autoSavedBcp, AUTO_SAVED_BCP_KEY, resolvedBcpId]);

  // Check if we have regulation data saved
  const hasRegulationData = useMemo(() => {
    console.log('[BCPRegulationPage] hasRegulationData calculation:', {
      hasRegulationFormData: !!navigationState.regulationFormData,
      regulationFormDataKeys: navigationState.regulationFormData ? Object.keys(navigationState.regulationFormData).length : 0,
      resolvedBcpId,
      hasBcpDocument: !!bcpDocument,
      hasRegulationSection: !!bcpDocument?.content?.sections?.regulation,
      bcpLoading,
    });

    // 最優先: navigationStateにregulationFormDataがある場合（ページ戻りの場合）
    if (navigationState.regulationFormData && Object.keys(navigationState.regulationFormData).length > 0) {
      console.log('[BCPRegulationPage] hasRegulationData = true (from navigationState.regulationFormData)');
      return true;
    }

    // bcpIdが指定されている場合（既存BCPの編集モード）
    if (resolvedBcpId !== 'new') {
      // bcpDocumentが読み込まれている場合、regulationセクションの有無を確認
      if (bcpDocument) {
        const hasRegulation = !!bcpDocument?.content?.sections?.regulation;
        console.log('[BCPRegulationPage] hasRegulationData = ' + hasRegulation + ' (from bcpDocument, edit mode)');
        return hasRegulation;
      }

      // bcpDocumentがまだ読み込まれていない場合は待機（再生成を防ぐため）
      console.log('[BCPRegulationPage] hasRegulationData = true (waiting for bcpDocument)');
      return true;
    }

    // 新規BCP作成モード (resolvedBcpId === 'new')
    console.log('[BCPRegulationPage] hasRegulationData = false (no regulation data found)');
    return false;
  }, [navigationState.regulationFormData, bcpDocument, resolvedBcpId, bcpLoading]);

  // 自動生成をスキップする条件:
  // 1. BCP読み込み中 (bcpLoading = true)
  // 2. 規程データが既に存在する (hasRegulationData = true)
  // 注: isEditModeは含めない（BCPが存在してもregulationが未作成の場合、生成が必要）
  const shouldSkipAutoGeneration = bcpLoading || hasRegulationData;

  // Extract restore policy data from BCP document if in edit mode
  const effectiveRestorePolicyData = useMemo(() => {
    if (restorePolicyData) {
      return restorePolicyData;
    }
    // If in edit mode and no restorePolicyData in state, extract from bcpDocument
    if (bcpDocument?.content?.sections?.restore_policy) {
      return bcpDocument.content.sections.restore_policy;
    }
    // Fallback: try to extract from form_data
    if (bcpDocument?.form_data) {
      const restorePolicyFields = ['company_info', 'disaster_risks', 'important_business', 'rto_setting', 'human_resources', 'facility_recovery', 'it_recovery', 'supply_chain', 'financial_plan', 'backup_location', 'remote_work', 'external_partners', 'inventory_utilization'];
      const extracted: Record<string, any> = {};
      restorePolicyFields.forEach(field => {
        if (bcpDocument.form_data![field]) {
          extracted[field] = bcpDocument.form_data![field];
        }
      });
      return Object.keys(extracted).length > 0 ? extracted : undefined;
    }
    return undefined;
  }, [restorePolicyData, bcpDocument]);

  // Skip auto-generation only if we already have regulation data
  const {
    isLoading: generationLoading,
    formData: generatedFormData,
    hasGenerated,
    error: generationError,
    jobStatus,
    elapsedSeconds,
    regenerate,
  } = useRegulationGenerationAsync({
    restorePolicyData: effectiveRestorePolicyData,
    savedFormData: navigationState.regulationFormData,
    skipAutoGeneration: shouldSkipAutoGeneration,
  });

  const form = useBCPDynamicForm({
    sections: REGULATION_SECTIONS,
    initialValues: initialValuesRef.current,
  });

  const { values, errors: fieldErrors, setFieldValue, reset, validate } = form;

  const hasUnsavedChanges = useMemo(() => {
    const baseline = initialValuesRef.current || {};
    const keys = new Set([
      ...Object.keys(baseline),
      ...Object.keys(values)
    ]);

    const changedKeys: string[] = [];
    const changedDetails: Record<string, { baseline: string; current: string }> = {};
    for (const key of keys) {
      const baselineVal = baseline[key] ?? '';
      const currentVal = values[key] ?? '';
      if (currentVal !== baselineVal) {
        changedKeys.push(key);
        if (changedKeys.length <= 3) {
          changedDetails[key] = { baseline: baselineVal, current: currentVal };
        }
      }
    }

    const result = changedKeys.length > 0;
    console.log('[BCPRegulationPage] hasUnsavedChanges calculation:', {
      result,
      changedCount: changedKeys.length,
      changedKeys: changedKeys.slice(0, 5),
      changedDetails,
      baselineKeys: Object.keys(baseline).length,
      valuesKeys: Object.keys(values).length,
      initialValuesVersion,
    });

    return result;
  }, [values, initialValuesVersion]);

  // BCP/Tenant変更を検知してリセット
  const bcpIdRef = useRef(resolvedBcpId);
  const tenantIdRef = useRef(resolvedTenantId);

  useEffect(() => {
    const bcpChanged = resolvedBcpId !== bcpIdRef.current;
    const tenantChanged = resolvedTenantId !== tenantIdRef.current;

    if (bcpChanged || tenantChanged) {
      console.log('[BCPRegulationPage] BCP or tenant changed, resetting state', {
        oldBcpId: bcpIdRef.current,
        newBcpId: resolvedBcpId,
        oldTenantId: tenantIdRef.current,
        newTenantId: resolvedTenantId
      });
      bcpIdRef.current = resolvedBcpId;
      tenantIdRef.current = resolvedTenantId;
      hasLoadedInitialData.current = false;
      // updateInitialValues(undefined)を呼ばない
      // 理由: 保存後にnavigate()でBCPIDが変わった場合、updateInitialValues(values)で設定した値がクリアされてしまう
      // hasLoadedInitialData.currentをfalseにすることで、次の初期データロードで正しい値が設定される
    }
  }, [resolvedBcpId, resolvedTenantId]);

  const aiConsultContext = useAIConsultContext({
    sections: REGULATION_SECTIONS,
    values,
    bcpType: 'basic',
    onFieldValueChange: setFieldValue,
  });

  // AI生成完了時にフォームに適用（自動保存はしない）
  useEffect(() => {
    console.log('[BCPRegulationPage] AI generation apply check:', {
      hasGenerated,
      hasGeneratedFormData: !!generatedFormData,
      generatedFormDataKeys: generatedFormData ? Object.keys(generatedFormData).length : 0,
      isEditMode,
      hasRegulationData,
      shouldSkipAutoGeneration,
    });

    // Apply generated data if:
    // 1. Generation completed and not in edit mode (normal flow from restore-policy)
    // 2. Generation completed in edit mode but no regulation data exists yet (triggered generation)
    // 3. Auto-generation was NOT skipped (to avoid applying saved data as "generated")
    const shouldApplyGenerated = hasGenerated && generatedFormData && (!isEditMode || !hasRegulationData) && !shouldSkipAutoGeneration;

    console.log('[BCPRegulationPage] shouldApplyGenerated:', shouldApplyGenerated);

    if (shouldApplyGenerated && Object.keys(generatedFormData).length > 0) {
      reset(generatedFormData);
      // AI生成された内容は未保存なので、initialValuesRefは更新しない
      // これにより、hasUnsavedChanges = trueとなり、離脱確認ダイアログが表示される
      console.log('[BCPRegulationPage] AI generation applied to form (not saved to DB, initialValues not updated)');
      setSuccessMessage(
        'AIによるBCP規定の下書きを生成しました。'
      );
      setGeneralErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGenerated, isEditMode, hasRegulationData, shouldSkipAutoGeneration]);

  // regulationFormDataがある場合は、それを復元（ページ戻りの場合）
  const hasRestoredFromNavigation = useRef(false);
  // DBから読み込んだ元のデータを保持(navigation state復元時の比較基準用)
  const dbLoadedDataRef = useRef<Record<string, string> | null>(null);

  // BCP/Tenant変更時に復元フラグとDBデータをリセット
  useEffect(() => {
    hasRestoredFromNavigation.current = false;
    dbLoadedDataRef.current = null;
    console.log('[BCPRegulationPage] Reset hasRestoredFromNavigation and dbLoadedData due to BCP/Tenant change');
  }, [resolvedBcpId, resolvedTenantId]);

  useEffect(() => {
    console.log('[BCPRegulationPage] Restore check:', {
      hasRegulationFormData: !!navigationState.regulationFormData,
      regulationFormDataKeys: navigationState.regulationFormData ? Object.keys(navigationState.regulationFormData).length : 0,
      hasRestoredFromNavigation: hasRestoredFromNavigation.current,
      isEditMode,
      hasLoadedInitialData: hasLoadedInitialData.current,
      hasDbLoadedData: !!dbLoadedDataRef.current,
    });

    if (navigationState.regulationFormData && Object.keys(navigationState.regulationFormData).length > 0 && !hasRestoredFromNavigation.current) {
      // 編集モードの場合は、初期データロード完了後に復元
      if (isEditMode && !hasLoadedInitialData.current) {
        console.log('[BCPRegulationPage] Restore skipped - waiting for initial data load in edit mode');
        return;
      }

      console.log('[BCPRegulationPage] Restoring regulationFormData from navigation state:', Object.keys(navigationState.regulationFormData));
      reset(navigationState.regulationFormData);

      // DBから読み込んだデータがあれば、それをinitialValuesRefとして設定
      // これにより、navigation stateから復元したデータとDBデータを比較できる
      if (dbLoadedDataRef.current) {
        updateInitialValues(dbLoadedDataRef.current);
        console.log('[BCPRegulationPage] Restored from navigation (set initialValues to DB data for comparison)');
      } else {
        console.log('[BCPRegulationPage] Restored from navigation (no DB data available, keeping current initialValues)');
      }

      hasRestoredFromNavigation.current = true;
      setSuccessMessage('前回の編集内容を復元しました。');
    } else {
      console.log('[BCPRegulationPage] Restore skipped');
    }
  }, [navigationState.regulationFormData, reset, isEditMode, updateInitialValues]);

  useEffect(() => {
    if (isEditMode && bcpDocument && !hasLoadedInitialData.current) {
      const rawData = getInitialFormData();
      console.log('[BCPRegulationPage] Raw initial form data:', {
        keys: Object.keys(rawData),
        sampleValues: Object.keys(rawData).slice(0, 3).reduce((acc, key) => {
          acc[key] = rawData[key];
          return acc;
        }, {} as Record<string, any>)
      });

      const initialData = extractRegulationFormValues(
        rawData as Record<string, string>
      );
      console.log('[BCPRegulationPage] Extracted regulation values:', {
        keys: Object.keys(initialData),
        fieldCount: Object.keys(initialData).length
      });

      // Check if we have regulationFormData to restore (navigation state takes priority)
      const hasNavigationData = navigationState.regulationFormData && Object.keys(navigationState.regulationFormData).length > 0;
      const dataToLoad = hasNavigationData
        ? navigationState.regulationFormData
        : initialData;

      console.log('[BCPRegulationPage] Loading data:', {
        source: hasNavigationData ? 'navigationState' : 'bcpDocument',
        keys: Object.keys(dataToLoad)
      });

      reset(dataToLoad);

      // Verify values after reset
      setTimeout(() => {
        console.log('[BCPRegulationPage] Values after reset:', {
          valuesKeys: Object.keys(values),
          valueCount: Object.keys(values).length,
          sampleValues: Object.keys(values).slice(0, 3).reduce((acc, key) => {
            acc[key] = values[key]?.substring(0, 50) || values[key];
            return acc;
          }, {} as Record<string, any>)
        });
      }, 100);

      // DBから読み込んだデータを保存(navigation state復元時の比較基準用)
      dbLoadedDataRef.current = initialData;
      console.log('[BCPRegulationPage] Saved DB loaded data to dbLoadedDataRef');

      if (hasNavigationData) {
        setSuccessMessage('前回の編集内容を復元しました。');
        // navigationStateから復元した場合は、initialValuesRefをDBデータに設定（未保存判定のため）
        updateInitialValues(initialData);
        console.log('[BCPRegulationPage] Loaded from navigationState (set initialValues to DB data for comparison)');
      } else {
        setSuccessMessage('BCPを編集モードで読み込みました。');
        // bcpDocumentから読み込んだ場合は、initialValuesRefを更新（保存済みデータ）
        updateInitialValues(initialData);
        console.log('[BCPRegulationPage] Loaded from bcpDocument (updated initialValues)');
      }
      setGeneralErrors([]);
      hasLoadedInitialData.current = true;
      hasRestoredFromNavigation.current = true; // 復元済みフラグを立てる
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, bcpDocument]);

  const handleFieldChange = (fieldId: string, value: string) => {
    setFieldValue(fieldId, value);
    if (generalErrors.length > 0) {
      setGeneralErrors([]);
    }
  };

  const handleSave = async () => {
    // 保存中の場合はスキップ
    if (isSaving) {
      return;
    }

    const isValid = validate();
    if (!isValid) {
      setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
      return;
    }

    setIsSaving(true);
    setSaveLoading(true);
    try {
      // 保存済みBCPがある場合はそのIDを使用、なければnull（新規作成）
      const bcpIdToSave = bcpDocument?.id || (resolvedBcpId === 'new' ? null : resolvedBcpId);

      const result = await bcpService.saveBasicSection(
        bcpIdToSave,
        'regulation',
        values,
        { current_step: 'regulation' },
        resolvedTenantId || undefined,
        'published'
      );

      // ローカルストレージから自動保存されたBCP情報を削除
      if (resolvedBcpId === 'new') {
        localStorage.removeItem(AUTO_SAVED_BCP_KEY);
      }

      // 保存成功後、現在の値を新しい初期値として設定
      console.log('[BCPRegulationPage] About to update initialValues with values:', {
        keysCount: Object.keys(values).length,
        sampleKeys: Object.keys(values).slice(0, 3),
      });
      updateInitialValues(values);
      console.log('[BCPRegulationPage] Updated initialValuesRef after save');

      // Update bcpId if this was a new document
      if (resolvedBcpId === 'new' && result.id) {
        console.log('[BCPRegulationPage] Navigating to new BCP ID:', result.id);
        beginInternalNavigation();
        navigate(`/bcp/${result.id}/regulation`, {
          state: {
            ...location.state,
            bcpId: result.id,
          },
          replace: true,
        });
      }

      setSuccessMessage(`保存が完了しました。`);
      setGeneralErrors([]);
    } catch (error) {
      console.error('BCP保存エラー:', error);
      setGeneralErrors(['BCPの保存に失敗しました。']);
    } finally {
      setSaveLoading(false);
      setIsSaving(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'docx') => {
    const isValid = validate();
    if (!isValid) {
      setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
      return;
    }

    try {
      const response = await exportState.execute(async () => {
        const exportData = {
          type: 'regulation',
          sections: REGULATION_SECTIONS.map((section) => ({
            title: section.title,
            fields: section.fields
              .filter((field) => field.type !== 'static')
              .map((field) => ({
                label: field.label,
                value: values[field.id] || '',
              })),
          })),
        };

        return await exportBCP(
          {
            ...values,
            export_data: JSON.stringify(exportData),
          },
          format
        );
      });

      if (response.success) {
        setSuccessMessage(`BCP規定を${format.toUpperCase()}形式でエクスポートしました`);
        setGeneralErrors([]);
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleExportPdf = async () => {
    // 保存中の場合はスキップ
    if (isSaving) {
      return;
    }

    setCurrentStep(3);

    // ダウンロード前に現在のフォーム内容を保存
    setIsSaving(true);
    try {
      console.log('[BCPRegulationPage] Auto-saving current form before download...');
      const bcpIdToSave = bcpDocument?.id || (resolvedBcpId === 'new' ? null : resolvedBcpId);
      const result = await bcpService.saveBasicSection(
        bcpIdToSave,
        'regulation',
        values,
        { current_step: 'regulation' },
        resolvedTenantId || undefined
      );
      console.log('[BCPRegulationPage] Auto-save successful:', result.id);
      setAutoSavedBcp(result);
      // 保存成功後、現在の値を新しい初期値として設定
      updateInitialValues(values);
    } catch (error) {
      console.error('[BCPRegulationPage] Auto-save failed:', error);
      // エラーは表示しない（保存ボタンから保存できるため）
    } finally {
      setIsSaving(false);
    }

    setIsDownloadModalOpen(true);
  };

  const handleNext = () => {
    // 「次へ」ボタンでのナビゲーション開始（ブロック解除）
    beginInternalNavigation();

    // BCP IDを決定（保存済みBCPのIDまたはURL指定のID）
    const nextBcpId = bcpDocument?.id || resolvedBcpId;

    // Build URL with tenantId if available
    let targetUrl = `/bcp/${nextBcpId}/initial-response`;
    if (resolvedTenantId) {
      targetUrl += `?tenantId=${resolvedTenantId}`;
    }

    console.log('[BCPRegulationPage] Navigating to next page without saving');
    navigate(targetUrl, {
      state: {
        ...location.state,
        regulationData: values,
        regulationFormData: values,
        restorePolicyData,
        restorePolicyFormData: navigationState.restorePolicyFormData,
        uploadedFiles,
        apiResponse,
      fromPage: 'regulation',
    },
  });
  };

  const handleBackToRestorePolicy = () => {
    beginInternalNavigation();
    navigate(
      `/bcp/${resolvedBcpId}/restore-policy?tenantId=${resolvedTenantId}`,
      { state: location.state }
    );
  };

  const aggregatedFieldErrors = useMemo(
    () => Object.values(fieldErrors),
    [fieldErrors]
  );

  const generationErrorInfo = useMemo(() => {
    if (!generationError) {
      return null;
    }

    const messages = [generationError];

    let title = '生成エラー';
    if (generationError.includes('タイムアウト')) {
      title = '⏱️ 処理タイムアウト';
      messages.push(
        '処理に5分以上かかっています。ページを再読み込みするか、時間を置いて再度お試しください。'
      );
    } else if (generationError.includes('Access denied')) {
      title = '🔒 アクセス権限エラー';
      messages.push('テナントへのアクセス権限がありません。管理者にお問い合わせください。');
    } else if (generationError.includes('Failed to create async job')) {
      title = '❌ ジョブ作成エラー';
      messages.push('AI生成ジョブの作成に失敗しました。時間を置いて再度お試しください。');
    }

    return {
      title,
      messages,
    };
  }, [generationError]);

  if (generationLoading || bcpLoading) {
    return (
      <div className="bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
              <Spinner size="lg" />
            </div>
            <h2 className="text-xl font-medium text-gray-900 mb-2">
              AIがBCP規定を生成中...
            </h2>
            <p className="text-gray-600">
              復旧方針の内容を分析して、最適なBCP規定の下書きを作成しています。
            </p>
            <p className="text-gray-600">数十秒程度かかることがあります。</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <BCPFormLayout
      currentStep={currentStep}
      showHeaderActions
      isEditMode={isEditMode}
      onSave={handleSave}
      onDownload={handleExportPdf}
      saveLoading={saveLoading}
      downloadLoading={exportState.isLoading}
      fieldContext={aiConsultContext.fieldContext}
      onAIContentApply={aiConsultContext.handleAIContentApply}
      bcpType={aiConsultContext.bcpType}
      topContent={
        <BasicBCPStepFlow
          currentStep="regulation"
          bcpId={resolvedBcpId}
          tenantId={resolvedTenantId}
          onStepNavigate={beginInternalNavigation}
        />
      }
    >
      <BCPFormContainer
        header={
          <>
            <BCPPageTitle
              title="基本版BCPシート（規定）"
              isEditMode={isEditMode}
              editInfo={
                bcpDocument
                  ? {
                      title: bcpDocument.content.title,
                      id: bcpDocument.id,
                    }
                  : undefined
              }
            />
            <div className="mt-6 flex justify-center">
              <GuideTopAI />
            </div>
          </>
        }
        alerts={
          <div className="space-y-4">
            {successMessage && <Alert type="success" message={successMessage} />}

            {generalErrors.length > 0 && (
              <Alert
                type="error"
                title="エラーが発生しました"
                messages={generalErrors}
              />
            )}

            {aggregatedFieldErrors.length > 0 && (
              <Alert
                type="error"
                title="入力内容を確認してください"
                messages={aggregatedFieldErrors}
              />
            )}

            {generationErrorInfo && (
              <div className="space-y-3">
                <Alert
                  type="error"
                  title={generationErrorInfo.title}
                  messages={generationErrorInfo.messages}
                />
                <Button
                  variant="primary"
                  onClick={regenerate}
                  loading={generationLoading}
                  className="ml-2"
                >
                  再試行
                </Button>
              </div>
            )}

            {exportState.error && (
              <Alert
                type="error"
                title="エクスポートエラー"
                messages={[exportState.error]}
              />
            )}
          </div>
        }
        footer={
          <>
            <ReferenceMaterialsPanel uploadedFiles={uploadedFiles} className="mt-6" />
            <div className="mt-8 flex justify-center gap-5">
              <RedButton
                onClick={handleBackToRestorePolicy}
              >
                戻る
              </RedButton>
              <RedButton onClick={handleNext}>次へ</RedButton>
            </div>
          </>
        }
      >
        {REGULATION_SECTIONS.map((section) => (
          <BCPSectionCard
            key={section.id}
            section={section}
            values={values}
            errors={fieldErrors}
            onChange={handleFieldChange}
            onAIConsult={aiConsultContext.handleAIConsult}
          />
        ))}
      </BCPFormContainer>
    </BCPFormLayout>

    {/* Download Modal */}
    <BCPDownloadModal
      isOpen={isDownloadModalOpen}
      onClose={() => {
        setIsDownloadModalOpen(false);
        setCurrentStep(2);
      }}
      bcpType="basic"
      bcpId={autoSavedBcp?.id || bcpDocument?.id || (resolvedBcpId !== 'new' ? resolvedBcpId : null)}
    />
  </>
  );
};

export default BCPRegulationPage;
